// ─── État de rattachement d'une opération, et vue par enveloppe ───
// « Savoir rapidement si une opération est déjà rattachée à un budget, avec
// les icônes rattachée / non rattachée / non visible » (Cédric, 11/08), et
// « une vue en arbre par budget avec des accordéons ; si pas dans un budget,
// la mettre sans budget ».
//
// Aucun accès Firestore ni React : testable tel quel.
import { repartitionDepense, rattachementsDeDepense, EPSILON } from './repartition.js';

// Les quatre états, dans l'ordre où ils réclament une action.
//   libre     — rien n'est affecté : c'est ce qui reste à faire ;
//   partielle — une part est affectée, une autre attend ;
//   rattachee — tout est affecté, il n'y a plus rien à décider ;
//   exclue    — hors calcul (virement de compte à compte). « Non visible »
//               dans les mots de Cédric : à ne pas considérer.
export const ETATS = ['libre', 'partielle', 'rattachee', 'exclue'];

export function etatOperation(operation, rattachements = []) {
  if (!operation) return 'libre';
  // L'exclusion prime : un virement interne n'a pas à être rattaché, et le
  // présenter comme « à faire » créerait une tâche qui n'existe pas.
  if (operation.exclu) return 'exclue';
  const { affecte, reste } = repartitionDepense(operation, rattachements);
  if (affecte < EPSILON) return 'libre';
  return reste > EPSILON ? 'partielle' : 'rattachee';
}

// ── Vue par enveloppe ────────────────────────────────────────────
// L'enveloppe EFFECTIVE d'une opération, par ordre de préférence :
//   1. celle de la prévision qu'elle honore — c'est l'usage réel de l'argent ;
//   2. à défaut, son rangement direct (`budgetId`) ;
//   3. sinon : sans enveloppe.
// Une opération répartie sur DEUX enveloppes apparaît dans les deux, avec la
// PART qui les concerne : la découper est le seul moyen de ne pas mentir sur
// les totaux — l'afficher en entier des deux côtés doublerait l'argent.
export function enveloppesDeOperation({ operation, rattachements = [], lignes = [] }) {
  const liens = rattachementsDeDepense(rattachements, operation?.id);
  if (!liens.length) {
    return [{ budgetId: operation?.budgetId || null, ligneId: null, montant: Math.abs(operation?.montant || 0), viaPrevision: false }];
  }
  const parLigne = new Map(lignes.map(l => [l?.id, l]));
  // `ligneId` (16/08) : l'arbre du compte montre désormais le niveau LIGNE
  // entre l'enveloppe et les opérations — chaque part sait qui elle honore.
  const parts = liens.map(r => ({
    budgetId: (parLigne.get(r.ligneId) || {}).budgetId || null,
    ligneId: r.ligneId || null,
    montant: Math.abs(r.montant || 0),
    viaPrevision: true,
  }));
  // Le RESTE non affecté relève du rangement direct, pas d'une prévision.
  const { reste } = repartitionDepense(operation, rattachements);
  if (reste > EPSILON) {
    parts.push({ budgetId: operation?.budgetId || null, ligneId: null, montant: reste, viaPrevision: false });
  }
  return parts;
}

/**
 * Regroupe des opérations par enveloppe, pour la vue en arbre.
 * Renvoie un tableau trié : les enveloppes nommées par montant décroissant,
 * puis « sans enveloppe » — toujours en DERNIER, car c'est un fourre-tout, pas
 * une catégorie de même rang.
 */
export function grouperParEnveloppe({ operations = [], rattachements = [], lignes = [], budgets = [] }) {
  const noms = new Map(budgets.map(b => [b?.id, b]));
  const parLigne = new Map(lignes.map(l => [l?.id, l]));
  const groupes = new Map();

  for (const op of operations) {
    if (!op) continue;
    for (const part of enveloppesDeOperation({ operation: op, rattachements, lignes })) {
      if (part.montant <= EPSILON) continue;
      const cle = part.budgetId || '__sans__';
      if (!groupes.has(cle)) {
        groupes.set(cle, {
          budgetId: part.budgetId || null,
          budget: noms.get(part.budgetId) || null,
          operations: [], total: 0, nb: 0,
          sousLignes: new Map(),
        });
      }
      const g = groupes.get(cle);
      g.operations.push({ operation: op, montant: part.montant, viaPrevision: part.viaPrevision });
      g.total = Math.round((g.total + (op.sens === 'revenu' ? 1 : -1) * part.montant) * 100) / 100;
      g.nb += 1;
      // Le niveau LIGNE (16/08) : sous chaque enveloppe, les parts se
      // regroupent par la prévision qu'elles honorent — `null` pour un
      // rangement direct, sans prévision.
      const cleLigne = part.ligneId || '__sans__';
      if (!g.sousLignes.has(cleLigne)) {
        g.sousLignes.set(cleLigne, {
          ligneId: part.ligneId || null,
          ligne: parLigne.get(part.ligneId) || null,
          operations: [], total: 0, nb: 0,
        });
      }
      const sl = g.sousLignes.get(cleLigne);
      sl.operations.push({ operation: op, montant: part.montant, viaPrevision: part.viaPrevision });
      sl.total = Math.round((sl.total + (op.sens === 'revenu' ? 1 : -1) * part.montant) * 100) / 100;
      sl.nb += 1;
    }
  }

  const out = [...groupes.values()].map(g => ({
    ...g,
    // Les lignes par montant décroissant ; « sans prévision » en dernier —
    // un reste à ranger, pas une catégorie.
    sousLignes: [...g.sousLignes.values()].sort((a, b) => {
      if (!a.ligneId) return 1;
      if (!b.ligneId) return -1;
      return Math.abs(b.total) - Math.abs(a.total);
    }),
  }));
  // MÊME ordre que Budget et Suivi (Cédric, 16/08 : « les 3 écrans sur le
  // même modèle ») : l'ordre d'origine des budgets, plus le poids — un
  // budget qui change de place selon l'écran ne se retrouve pas.
  const rang = new Map(budgets.map((b, i) => [b?.id, i]));
  out.sort((a, b) => {
    if (!a.budgetId) return 1;          // « sans enveloppe » toujours en dernier
    if (!b.budgetId) return -1;
    return (rang.get(a.budgetId) ?? 1e9) - (rang.get(b.budgetId) ?? 1e9);
  });
  return out;
}

// Décompte par état, pour un résumé en tête d'écran : « 12 à rattacher ».
export function compterEtats(operations = [], rattachements = []) {
  const c = { libre: 0, partielle: 0, rattachee: 0, exclue: 0 };
  for (const op of operations) c[etatOperation(op, rattachements)] += 1;
  return c;
}
